/* eslint-disable jsx-a11y/no-static-element-interactions */
// React Dependencies
import React, { useEffect, useRef, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-maps-react-markers';
import { toast } from 'react-toastify';
import { CircularProgress } from '@material-ui/core';

// Internal Dependencies
import { IsArray, IsEmpty } from '../common/checks';
import colours from '../styles/colours';
import { GetUserCurrentLocation, isCoordOutOfSG, Sleep } from '../common/util';
import { Error } from '../common/constants';
import Images from './basics/Images';

const initialRegion = {
  /**
   * Note:
   * If we change the latitudeDelta and longitudeDelta values
   * here, please test iOS and ensure we can pan and zoom the map.
   */
  latitudeDelta:  0.30900000000088657,
  longitudeDelta: 0.1887107197025557,
  latitude:       1.3747008307647381,
  longitude:      103.82751575950175,
};

const MMap = (props) => {
  const [markersData, setMarkersData] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: initialRegion.latitude, lng: initialRegion.longitude });
  const [isLoadingGps, setIsLoadingGps] = useState(false);

  const mapRef = useRef(null);

  const onMapLoaded = ({ map, maps }) => {
    mapRef.current = map;
    if (!IsEmpty(props.onMapLoaded)) {
      props.onMapLoaded({ map, maps });
    }
  };

  const onGPSButtonClick = async () => {
    setIsLoadingGps(true);

    try {
      await Sleep(200);

      const coords = await GetUserCurrentLocation(5000);
      if (IsEmpty(coords)) {
        toast.info(Error.LocationUnavailable);
      } else {
        const isOutOfSG = isCoordOutOfSG(coords);
        if (isOutOfSG) {
          toast.info('Seems like you are outside of Singapore. This feature is currently available in Singapore only.');
        } else if (!IsEmpty(props.onGPSButtonClick)) {
          props.onGPSButtonClick(coords);
        } else {
          if (IsEmpty(mapRef.current)) return;
          mapRef.current.panTo(coords);
          mapRef.current.setZoom(17);
        }
      }
    } catch (err) {
      if (err.message === Error.DefaultUserDeniedGeolocation) {
        toast.info(Error.UserDeniedGeolocationService);
      } else {
        toast.info(Error.UnableToGetUserCurrentLocation);
      }
    } finally {
      setIsLoadingGps(false);
    }
  };

  useEffect(() => {
    if (!IsEmpty(props.center) && !IsEmpty(props.center.latitude) && !IsEmpty(props.center.longitude)) {
      setMapCenter({ lat: props.center.latitude, lng: props.center.longitude });
    } else {
      setMapCenter({ lat: initialRegion.latitude, lng: initialRegion.longitude });
    }
  }, [
    !IsEmpty(props.center) ? props.center.latitude : undefined,
    !IsEmpty(props.center) ? props.center.longitude : undefined,
  ]);

  useEffect(() => {
    // this is needed to ensure selected marker is the last sibling in the render component
    // so that its marker icon and info box won't be covered by other markers
    setMarkersData((prev) => {
      if (IsArray(props.markersData)) {
        return IsEmpty(props.selectedMarker && props.selectedMarker.poiId)
          ? props.markersData
          : [
            ...props.markersData.filter(
                (m) => m.poiId !== props.selectedMarker.poiId,
              ),
            ...props.markersData.filter(
                (m) => m.poiId === props.selectedMarker.poiId,
              ),
          ];
      }
      return prev;
    });
  }, [
    props.selectedMarker && props.selectedMarker.poiId,
    props.markersData,
  ]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className=""
      style={{ height: '100%' }}
      onClick={() => {
        props.onMapClick();
      }}
      onTouchStart={() => {
        props.onMapTouch();
      }}
    >
      <GoogleMapReact
        apiKey={props.GOOGLE_MAP_API_KEY}
        defaultCenter={mapCenter}
        options={{
          disableDefaultUI:  true,
          minZoom:           11,
          maxZoom:           20,
          clickableIcons:    false,
          gestureHandling:   props.disabled ? 'none' : 'greedy',
          fullscreenControl: false,
          mapTypeControl:    false,
          streetViewControl: false,
          ...props.options,
        }}
        defaultZoom={12}
        loadingContent={null}
        onGoogleApiLoaded={onMapLoaded}
        onChange={props.onRegionChangeDebounce}
      >
        {
          !IsEmpty(props.shopperLocation) && !IsEmpty(props.shopperLocation.lat) && !IsEmpty(props.shopperLocation.lng) && (
            <img
              src={Images.Local.GPSMarker}
              width={30}
              height={30}
              style={{
                transform:       `translate(-50%, -50%)`,
                transformOrigin: 'center bottom',
              }}
              // eslint-disable-next-line react/no-unknown-property
              lat={props.shopperLocation.lat}
              // eslint-disable-next-line react/no-unknown-property
              lng={props.shopperLocation.lng}
              // eslint-disable-next-line react/no-unknown-property
              zIndex={99}
              alt="location"
            />
          )
        }
        {
          markersData.map((marker, index) => (
            // The custom markerIcon component should expose these props:
            // marker, selectedMarker and onMarkerClick
            <props.markerIcon
              key={`lat-${marker.latitude}-lng-${marker.longitude}`}
              lat={marker.latitude}
              lng={marker.longitude}
              marker={marker}
              markerSize={props.markerSize}
              onMarkerClick={props.onMarkerClick}
              onMarkerClose={props.onMarkerClose}
              selectedMarker={props.selectedMarker}
              zIndex={index}
            />
          ))
        }
        {
          !IsEmpty(props.staticMarkersData) && props.staticMarkersData.map((sm) => (
            <props.staticMarkerIcon
              key={`static-lat-${sm.latitude}-lng-${sm.longitude}`}
              lat={sm.latitude}
              lng={sm.longitude}
              markerSize={props.staticMarkerSize}
              marker={sm}
            />
          ))
        }
      </GoogleMapReact>

      {
        props.showGPSButton && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div className="gps__container" onClick={onGPSButtonClick}>
            {
              isLoadingGps ? (
                <CircularProgress color={colours.Primary} size={21} />
              ) : (
                <img
                  src={Images.Local.GPSLocate}
                  alt={Images.Local.GPSLocate}
                  // Transform is required to display the bottom tip of the marker icon
                  // at the lat lng position
                  style={{ color: colours.Gray, width: 25, height: 25, cursor: 'pointer' }}
                />
              )
            }
          </div>
        )
      }
    </div>
  );
};

MMap.propTypes = {
  disabled:           PropTypes.bool,
  GOOGLE_MAP_API_KEY: PropTypes.string.isRequired,
  markersData:        PropTypes.arrayOf(PropTypes.shape({
    latitude:  PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    icon:      PropTypes.func,
  })),
  // eslint-disable-next-line react/no-unused-prop-types
  markerIcon:        PropTypes.func.isRequired,
  markerSize:        PropTypes.number,
  staticMarkerSize:  PropTypes.number,
  staticMarkersData: PropTypes.arrayOf(PropTypes.shape({
    latitude:  PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  })),
  // eslint-disable-next-line react/no-unused-prop-types
  staticMarkerIcon: PropTypes.func,
  showGPSButton:    PropTypes.bool,
  selectedMarker:   PropTypes.shape({
    poiId: PropTypes.number,
  }),
  shopperLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  center: PropTypes.shape({
    latitude:  PropTypes.number,
    longitude: PropTypes.number,
  }),
  options:                PropTypes.shape({}),
  onMarkerClick:          PropTypes.func,
  onMarkerClose:          PropTypes.func,
  onMapClick:             PropTypes.func,
  onMapTouch:             PropTypes.func,
  onRegionChangeDebounce: PropTypes.func,
  onMapLoaded:            PropTypes.func,
  onGPSButtonClick:       PropTypes.func,
};

MMap.defaultProps = {
  disabled:               false,
  markersData:            [],
  markerSize:             40,
  showGPSButton:          false,
  staticMarkerSize:       36,
  staticMarkersData:      [],
  staticMarkerIcon:       () => {},
  shopperLocation:        null,
  selectedMarker:         null,
  center:                 null,
  options:                {},
  onMapClick:             () => {},
  onMarkerClose:          () => {},
  onMapTouch:             () => {},
  onMarkerClick:          () => {},
  onRegionChangeDebounce: () => {},
  onMapLoaded:            () => {},
  onGPSButtonClick:       null,
};

export default MMap;
